import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Target, Compass, Crown, TrendingUp } from "lucide-react";
import { Reveal } from "../components/motion";
import { Seo } from "../components/Seo";
import { CASE_STUDIES } from "../data/content";

const Block = ({ icon: Icon, label, children }) => (
  <Reveal className="grid md:grid-cols-[260px_1fr] gap-6 md:gap-16 py-14 border-t hairline">
    <div className="flex items-start gap-4">
      <span className="p-3 rounded-full shrink-0" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}><Icon size={20} /></span>
      <h3 className="display text-2xl sm:text-3xl" style={{ color: "var(--text)" }}>{label}</h3>
    </div>
    <div>{children}</div>
  </Reveal>
);

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);
  if (!cs) return <Navigate to="/case-studies" replace />;

  const idx = CASE_STUDIES.findIndex((c) => c.slug === slug);
  const prev = CASE_STUDIES[(idx - 1 + CASE_STUDIES.length) % CASE_STUDIES.length];
  const next = CASE_STUDIES[(idx + 1) % CASE_STUDIES.length];
  const others = CASE_STUDIES.filter((c) => c.slug !== slug).slice(0, 3);

  return (
    <div data-testid="case-detail-page">
      <Seo
        path={`/case-studies/${cs.slug}`}
        type="article"
        description={`${cs.title} — ${cs.summary}`}
        image={cs.image}
        keywords={`${cs.client}, ${cs.tag}, UX case study, Yuva Kumar`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: cs.title,
          description: cs.summary,
          image: cs.image,
          author: { "@type": "Person", name: "Yuva Kumar", url: "https://yuvadesign.in/" },
          about: cs.client,
          keywords: cs.tag,
          mainEntityOfPage: `https://yuvadesign.in/case-studies/${cs.slug}`,
        }}
      />
      {/* Hero */}
      <section className="pt-36">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal>
            <Link to="/case-studies" data-testid="back-to-cases" className="inline-flex items-center gap-2 text-sm mb-10 hover:opacity-70 transition-opacity" style={{ color: "var(--muted-t)" }}>
              <ArrowLeft size={16} /> All case studies
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="eyebrow">{cs.tag}</span>
              <span className="text-sm" style={{ color: "var(--muted-t)" }}>{cs.client} · {cs.year}</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="display text-5xl sm:text-8xl max-w-[16ch]" style={{ color: "var(--text)" }}>{cs.title}</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-xl leading-relaxed" style={{ color: "var(--muted-t)" }}>{cs.summary}</p>
          </Reveal>
        </div>
      </section>

      {/* Cover media */}
      <section className="py-16">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal>
            <div className="overflow-hidden" style={{ borderRadius: "2px", border: "1px solid var(--line)" }}>
              <div className="aspect-[21/9] overflow-hidden">
                {cs.video ? (
                  <video
                    className="w-full h-full object-cover"
                    autoPlay muted loop playsInline preload="metadata"
                    poster={cs.image}
                    data-testid="case-video"
                  >
                    <source src={cs.video} type="video/mp4" />
                  </video>
                ) : (
                  <img src={cs.image} alt={cs.title} className="w-full h-full object-cover" style={{ filter: "grayscale(0.2) contrast(1.05)" }} />
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Deep dive */}
      <section className="pb-16">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Block icon={Target} label="Problem Statement">
            <p className="text-lg leading-relaxed" style={{ color: "var(--muted-t)" }}>{cs.problem}</p>
          </Block>

          <Block icon={Crown} label="My Leadership Role">
            <p className="text-lg leading-relaxed" style={{ color: "var(--muted-t)" }}>{cs.role}</p>
          </Block>

          <Block icon={Compass} label="UX Strategy & Methodology">
            <ul className="space-y-5">
              {cs.strategy.map((s, i) => (
                <li key={i} className="flex gap-4">
                  <span className="display text-xl shrink-0" style={{ color: "var(--accent)" }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg leading-relaxed" style={{ color: "var(--muted-t)" }}>{s}</span>
                </li>
              ))}
            </ul>
          </Block>

          <Block icon={TrendingUp} label="Business Outcome">
            <div className="grid sm:grid-cols-3 gap-px" style={{ background: "var(--line)" }}>
              {cs.outcome.map((o, i) => (
                <div key={i} className="p-8" style={{ background: "var(--surface)" }} data-testid={`outcome-${i}`}>
                  <span className="display text-4xl sm:text-5xl block" style={{ color: "var(--accent)" }}>{o.value}</span>
                  <span className="text-sm mt-3 block" style={{ color: "var(--muted-t)" }}>{o.label}</span>
                </div>
              ))}
            </div>
          </Block>
        </div>
      </section>

      {/* Detailed process / impact / ROI — connect */}
      <section className="pb-8" data-testid="case-connect">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal>
            <div className="p-8 sm:p-12 surface-card" style={{ borderRadius: "var(--radius-card)", border: "1px solid var(--line)" }}>
              <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 items-center">
                <div>
                  <p className="eyebrow mb-4">Go deeper</p>
                  <h3 className="display text-2xl sm:text-4xl mb-4" style={{ color: "var(--text)" }}>
                    Want the detailed process, business impact & ROI?
                  </h3>
                  <p className="text-base leading-relaxed max-w-2xl" style={{ color: "var(--muted-t)" }}>
                    The full breakdown — research artifacts, methodology, stakeholder maps, before/after metrics and the
                    return-on-investment model — is shared on request under NDA. Let&apos;s connect and I&apos;ll walk you through it.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-6 text-xs uppercase tracking-widest" style={{ color: "var(--accent)" }}>
                    <span className="px-3 py-1.5 rounded-full" style={{ background: "var(--accent-soft)" }}>Process walkthrough</span>
                    <span className="px-3 py-1.5 rounded-full" style={{ background: "var(--accent-soft)" }}>Business impact</span>
                    <span className="px-3 py-1.5 rounded-full" style={{ background: "var(--accent-soft)" }}>ROI model</span>
                  </div>
                </div>
                <div className="lg:justify-self-end">
                  <Link
                    to="/contact"
                    data-testid="case-connect-cta"
                    className="group inline-flex items-center gap-4 px-8 py-5 rounded-full"
                    style={{ background: "var(--accent)", color: "var(--bg)" }}
                  >
                    <span className="text-sm uppercase tracking-widest">Please connect</span>
                    <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Prev / Next quick nav */}
      <section className="border-t border-b hairline" data-testid="case-prevnext">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 grid sm:grid-cols-2">
          <Link to={`/case-studies/${prev.slug}`} data-testid="case-prev" className="group py-10 sm:py-12 sm:pr-10 flex items-center gap-4 sm:border-r hairline">
            <ArrowLeft size={20} style={{ color: "var(--accent)" }} className="shrink-0 transition-transform group-hover:-translate-x-1" />
            <div>
              <p className="eyebrow mb-1">Previous</p>
              <p className="display text-xl sm:text-2xl" style={{ color: "var(--text)" }}>{prev.title}</p>
            </div>
          </Link>
          <Link to={`/case-studies/${next.slug}`} data-testid="case-next" className="group py-10 sm:py-12 sm:pl-10 flex items-center justify-end gap-4 text-right">
            <div>
              <p className="eyebrow mb-1">Next</p>
              <p className="display text-xl sm:text-2xl" style={{ color: "var(--text)" }}>{next.title}</p>
            </div>
            <ArrowUpRight size={20} style={{ color: "var(--accent)" }} className="shrink-0 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </section>

      {/* More case studies */}
      <section className="py-24" data-testid="more-case-studies">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <h2 className="display text-3xl sm:text-5xl" style={{ color: "var(--text)" }}>More case studies</h2>
            <Link to="/case-studies" data-testid="view-all-cases-detail" className="group flex items-center gap-2 text-sm uppercase tracking-widest" style={{ color: "var(--accent)" }}>
              View all <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((o, i) => (
              <Reveal key={o.slug} delay={i * 0.08}>
                <Link to={`/case-studies/${o.slug}`} data-testid={`more-case-${o.slug}`} className="group block">
                  <div className="overflow-hidden mb-5" style={{ borderRadius: "2px", border: "1px solid var(--line)" }}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={o.image} alt={o.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ filter: "grayscale(0.3) contrast(1.05)" }} />
                    </div>
                  </div>
                  <span className="eyebrow">{o.tag}</span>
                  <h3 className="display text-2xl mt-2 group-hover:opacity-70 transition-opacity" style={{ color: "var(--text)" }}>{o.title}</h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Next CTA */}
      <section className="py-24">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal>
            <Link to="/contact" data-testid="case-contact-cta" className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 p-10 sm:p-16 surface-card" style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)" }}>
              <h2 className="display text-3xl sm:text-5xl max-w-[18ch]" style={{ color: "var(--text)" }}>Have a problem worth solving? Let&apos;s scope it together.</h2>
              <span className="shrink-0 flex items-center gap-3 px-8 py-5 rounded-full" style={{ background: "var(--accent)", color: "var(--bg)" }}>
                <span className="text-sm uppercase tracking-widest">Let&apos;s Talk Business</span>
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetail;
